import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Swords, X, Wallet, Loader2 } from "lucide-react";
import { friendsStore } from "../store/friendsStore";
import type { InviteGame } from "../auth/api";
import { useUsdcBalance, formatUSDC, parseUSDC } from "../hooks/useGames";
import { SOLANA_NETWORK, LAMPORTS_PER_SOL, solToLamports } from "../contracts/solana/config";
import { useSolanaMoney } from "../hooks/useSolanaMoney";
import { useSolUsdPrice } from "../hooks/useSolUsdPrice";
import Avatar from "./Avatar";
import { avatarUrl } from "../auth/api";

const MIN_STAKE = 1;
const MAX_STAKE = 10_000;

// Games that can be challenged to a private custom match (no Snake). Icons,
// colors and names are the EXACT values from the "Send Challenge" design
// prototype — keyed by the InviteGame id the escrow / sendInvite expects.
const GAMES: { id: InviteGame; name: string; color: string; icon: ReactNode }[] = [
  {
    id: "fps",
    name: "FPS Arena",
    color: "#ff5722",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff5722" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 2v5M12 17v5M2 12h5M17 12h5" />
        <circle cx="12" cy="12" r="2" fill="#ff5722" stroke="none" />
      </svg>
    ),
  },
  {
    id: "pong",
    name: "Pong",
    color: "#7dd3fc",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="7" width="2.6" height="10" rx="1.3" fill="#7dd3fc" />
        <rect x="18.4" y="7" width="2.6" height="10" rx="1.3" fill="#7dd3fc" />
        <circle cx="12" cy="12" r="2.2" fill="#7dd3fc" />
      </svg>
    ),
  },
  {
    id: "td",
    name: "Tower Defense",
    color: "#c084fc",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c084fc" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 20v-6l3-2v-3l3-3 3 3v3l3 2v6" />
        <rect x="10" y="20" width="4" height="2" fill="#c084fc" stroke="none" />
      </svg>
    ),
  },
  {
    id: "math",
    name: "Mental Math",
    color: "#7dd3fc",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round">
        <path d="M5 8h6M8 5v6M5 16h6M14 7l5 5M19 7l-5 5M14 17h5" />
      </svg>
    ),
  },
];

type Currency = "USD" | "SOL";
// Preset chips per display currency. USD tiers are the canonical whole-dollar
// stakes; the SOL tiers are a display convenience (rounded to a whole-dollar
// stake on send — the escrow is always denominated in integer USD).
const TIERS: Record<Currency, number[]> = { USD: [1, 5, 25, 100], SOL: [0.05, 0.25, 1, 5] };

interface Result {
  type: "success" | "error";
  title: string;
  msg: ReactNode;
}

interface ChallengeModalProps {
  target: { address: string; handle: string; avatarVersion: number };
  walletAddress: string | null;
  onClose: () => void;
  /** Open the wallet panel so the sender can top up before challenging. */
  onShowWallet?: () => void;
}

// rgba() from a #rrggbb hex — themes the game card with each game's accent.
const hexA = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// Solana mark for the amount field's currency sign (matches the Withdraw card).
const SolMark = () => (
  <svg width="21" height="16" viewBox="0 0 397.7 311.7" aria-label="SOL">
    <defs>
      <linearGradient id="solGradChallenge" x1="360.9" y1="-37.5" x2="141.2" y2="383.3" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#00ffa3" />
        <stop offset="1" stopColor="#dc1fff" />
      </linearGradient>
    </defs>
    <path fill="url(#solGradChallenge)" d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H16.5c-5.8 0-8.7-7-4.6-11.1l52.7-62.7z" />
    <path fill="url(#solGradChallenge)" d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H16.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z" />
    <path fill="url(#solGradChallenge)" d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.6z" />
  </svg>
);

// Animated ring + mark, drawn via the sc-draw-* stroke-dash classes (index.css).
// Success = an ENVELOPE (rect + flap); error = an X.
const ResultIcon = ({ type }: { type: Result["type"] }) =>
  type === "success" ? (
    <svg width="78" height="78" viewBox="0 0 76 76" className="block" aria-hidden="true">
      <circle className="sc-draw-ring" cx="38" cy="38" r="33" stroke="#5fcf80" strokeWidth="3" fill="rgba(95,207,128,0.07)" strokeLinecap="round" />
      <rect className="sc-draw-mark" x="23" y="29" width="30" height="19" rx="3" stroke="#5fcf80" strokeWidth="3.2" fill="none" />
      <path className="sc-draw-mark" d="M24.5 31.5L38 42l13.5-10.5" stroke="#5fcf80" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="78" height="78" viewBox="0 0 76 76" className="block" aria-hidden="true">
      <circle className="sc-draw-ring" cx="38" cy="38" r="33" stroke="#ff5a5a" strokeWidth="3" fill="rgba(255,90,90,0.07)" strokeLinecap="round" />
      <path className="sc-draw-mark" d="M28 28l20 20M48 28L28 48" stroke="#ff5a5a" strokeWidth="4" fill="none" strokeLinecap="round" />
    </svg>
  );

/**
 * ChallengeModal — pick a game + stake and send a friend a challenge to a
 * private custom match. The stake is canonically an integer USD amount (the
 * escrow rounds to whole dollars); the USDC / SOL lock happens when each
 * player enters the match, so here we only surface the wallet balance so the
 * sender isn't challenging blind.
 *
 * Styling follows the "Send Challenge" design from the Claude Design
 * "Hupper - Pong" handoff: warm gradient card, glowing top hairline, a
 * ‹ / › game carousel, an amount field with a SOL⇄USD display toggle (live
 * useSolUsdPrice rate; the invite is always sent in USD), an orange pill
 * button with a shine sweep, and an animated full-card success / error result
 * state that auto-dismisses. Previous version in legacy/ via git history.
 */
export default function ChallengeModal({ target, walletAddress, onClose, onShowWallet }: ChallengeModalProps) {
  const [gi, setGi] = useState(0);
  const [cur, setCur] = useState<Currency>("USD");
  const [amount, setAmount] = useState("5");
  const [sending, setSending] = useState(false);

  // Game-carousel swap animation + full-card result state.
  const [swap, setSwap] = useState<{ transform: string; opacity: number } | null>(null);
  const [noTrans, setNoTrans] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [closing, setClosing] = useState(false);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const swapBusy = useRef(false);
  const timers = useRef<number[]>([]);
  const mounted = useRef(true);

  const meta = GAMES[gi] ?? GAMES[0];
  const game = meta.id;

  // EVM money read — nulled on Solana so no ethers balanceOf(base58) fires
  // (and the unconfigured-USDC zero can't wrongly block the challenge).
  const balance = useUsdcBalance(SOLANA_NETWORK ? null : walletAddress, 15_000);
  // Solana money read — the Vault `available` the settler locks stakes from.
  const sol = useSolanaMoney(SOLANA_NETWORK ? walletAddress : null);
  const { price: solPrice } = useSolUsdPrice();
  const priceOk = solPrice != null && solPrice > 0;
  // SOL display is only offered on Solana with a live rate to convert against.
  const canToggle = SOLANA_NETWORK && priceOk;

  const balanceUsd = SOLANA_NETWORK
    ? sol.available != null && priceOk
      ? (Number(sol.available) / LAMPORTS_PER_SOL) * solPrice
      : 0
    : Number(formatUSDC(balance.value));

  // Canonical stake — always an integer USD amount. In SOL mode the typed
  // value is converted at the live rate and rounded to a whole dollar.
  const entered = parseFloat((amount || "").replace(",", "."));
  const stake = useMemo(() => {
    if (!Number.isFinite(entered) || entered <= 0) return 0;
    if (cur === "SOL") return priceOk ? Math.round(entered * solPrice) : 0;
    return Math.floor(entered);
  }, [entered, cur, priceOk, solPrice]);

  const stakeMinor = stake > 0 ? parseUSDC(stake) : 0n;
  // Confident "below the stake" reading only — not while loading or after an
  // RPC error (so a transient read can't wrongly block the challenge).
  const insufficient = SOLANA_NETWORK
    ? sol.available != null && priceOk && stake > 0 && sol.available < solToLamports(stake / solPrice)
    : !balance.loading && !balance.error && balance.value < stakeMinor;

  const valid = entered > 0 && stake >= MIN_STAKE && stake <= MAX_STAKE;
  const canSend = valid && !insufficient && !sending && !result;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // On unmount, flag the component and clear pending timers so neither a result
  // timer nor an in-flight send() continuation can setState after it closes.
  useEffect(
    () => () => {
      mounted.current = false;
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  const fmt = (v: number, c: Currency) =>
    c === "USD" ? "$" + v.toFixed(2) : Math.round(v * 1e4) / 1e4 + " SOL";

  // Winner takes the whole pot minus the 10% platform fee.
  const potUsd = stake * 2 * 0.9;
  const winnerText = !valid
    ? fmt(0, cur)
    : cur === "USD"
      ? fmt(potUsd, "USD")
      : fmt(priceOk ? potUsd / solPrice : 0, "SOL");

  // ── Game carousel — slide the card out, swap content, slide the next in.
  const changeGame = (dir: 1 | -1) => {
    if (swapBusy.current) return;
    swapBusy.current = true;
    setNoTrans(false);
    setSwap({ transform: `translateX(${dir > 0 ? -14 : 14}px) scale(0.97)`, opacity: 0 });
    timers.current.push(
      window.setTimeout(() => {
        setGi((g) => (g + dir + GAMES.length) % GAMES.length);
        // Jump to the opposite side with no transition, then release so the
        // new card animates back to center.
        setNoTrans(true);
        setSwap({ transform: `translateX(${dir > 0 ? 14 : -14}px) scale(0.97)`, opacity: 0 });
        timers.current.push(
          window.setTimeout(() => {
            setNoTrans(false);
            setSwap(null);
            swapBusy.current = false;
          }, 24),
        );
      }, 200),
    );
  };

  const selectChip = (v: number) => setAmount(String(v));

  const toggleCurrency = () => {
    if (!canToggle) return;
    const v = parseFloat((amount || "").replace(",", "."));
    const next: Currency = cur === "USD" ? "SOL" : "USD";
    if (Number.isFinite(v) && v > 0) {
      setAmount(next === "SOL" ? (v / solPrice).toFixed(4) : String(Math.max(MIN_STAKE, Math.round(v * solPrice))));
    }
    setCur(next);
  };

  // ── Result state: lock the card height, swap the content, auto-dismiss.
  // Success animates the card out and closes; error returns to the form.
  const showResult = (r: Result) => {
    setCardHeight(cardRef.current?.offsetHeight ?? null);
    setResult(r);
    timers.current.push(
      window.setTimeout(() => {
        if (r.type === "success") {
          setClosing(true);
          timers.current.push(window.setTimeout(onClose, 460));
        } else {
          setResult(null);
          setCardHeight(null);
        }
      }, 2500),
    );
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const ok = await friendsStore.sendInvite(target, game, stake);
    if (!mounted.current) return; // modal closed mid-send — don't touch state
    setSending(false);
    if (ok) {
      showResult({
        type: "success",
        title: "Challenge sent",
        msg: (
          <>
            <b>{meta.name}</b> · {fmt(cur === "USD" ? stake : entered, cur)} stake.
            <br />
            {target.handle} will get a notification to accept.
          </>
        ),
      });
    } else {
      showResult({
        type: "error",
        title: "Couldn’t send challenge",
        msg: "Something went wrong — nothing left your balance. Try again.",
      });
    }
  };

  const openWallet = () => {
    onShowWallet?.();
    onClose();
  };

  const dots = GAMES.map((g, i) => (
    <span
      key={g.id}
      className="block h-1.5 rounded-full transition-all duration-200"
      style={{ width: i === gi ? 18 : 6, background: i === gi ? meta.color : "#322a22" }}
    />
  ));

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      onClick={onClose}
      role="dialog"
      aria-label="Send a challenge"
    >
      <div
        ref={cardRef}
        style={cardHeight != null ? { height: cardHeight } : undefined}
        className={`relative w-full max-w-[460px] rounded-[24px] border border-[#322a22] bg-[linear-gradient(180deg,rgba(22,18,15,0.98),rgba(10,8,6,0.99))] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.85)] overflow-hidden text-[#f5efe6] transition-[opacity,transform] duration-[400ms] ease-out ${closing ? "opacity-0 scale-[0.96] translate-y-2" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glowing top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[3px] opacity-90 z-[3] bg-[linear-gradient(90deg,transparent,#ff5722_30%,#ff8a4a_50%,#ff5722_70%,transparent)]" />

        {result ? (
          /* ── Result state (success / error) ── */
          <div className="h-full min-h-[340px] flex flex-col items-center justify-center text-center px-[34px] py-[54px] animate-wd-state-in">
            <div
              className="mb-[22px]"
              style={{
                filter:
                  result.type === "success"
                    ? "drop-shadow(0 0 22px rgba(95,207,128,0.35))"
                    : "drop-shadow(0 0 22px rgba(255,90,90,0.35))",
              }}
            >
              <ResultIcon type={result.type} />
            </div>
            <div className="text-[22px] font-bold tracking-[-0.3px] mb-2">{result.title}</div>
            <div className="text-[14px] text-[#8a7f72] leading-[1.55] max-w-[300px] [&_b]:text-[#f5efe6] [&_b]:font-semibold">
              {result.msg}
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-6 pt-[22px] pb-5 border-b border-[#241e18]">
              <Swords className="w-6 h-6 text-[#ff5722] shrink-0" strokeWidth={2} />
              <h1 className="flex-1 m-0 text-[20px] font-bold tracking-[-0.3px]">Send a challenge</h1>
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 w-[38px] h-[38px] rounded-full bg-white/[0.05] hover:bg-white/10 flex items-center justify-center text-[#8a7f72] hover:text-[#f5efe6] transition-all duration-150"
              >
                <X className="w-[15px] h-[15px]" strokeWidth={2.2} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 pt-[22px] pb-6">
              {/* Opponent */}
              <div className="flex items-center gap-[15px] mb-[26px]">
                <div
                  className="shrink-0 rounded-full overflow-hidden"
                  style={{ border: "1.5px solid rgba(255,138,74,0.35)", boxShadow: "0 0 20px rgba(255,138,74,0.2)" }}
                >
                  <Avatar
                    address={target.address}
                    handle={target.handle}
                    src={avatarUrl(target.address, target.avatarVersion)}
                    size={56}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-[20px] font-bold tracking-[-0.3px] leading-[1.1] truncate">{target.handle}</div>
                  <div className="text-[13.5px] text-[#8a7f72] mt-[3px] leading-[1.4]">
                    will get a notification to accept or decline
                  </div>
                </div>
              </div>

              {/* Game selector — arrows + single card + dots */}
              <div className="flex items-stretch gap-3">
                <button
                  onClick={() => changeGame(-1)}
                  aria-label="Previous game"
                  className="w-10 shrink-0 inline-flex items-center justify-center text-[#8a7f72] hover:text-[#f5efe6] active:scale-90 transition-[color,transform] duration-150"
                >
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div
                  className="flex-1 min-w-0 rounded-2xl px-[22px] py-5 flex items-center gap-4 relative overflow-hidden border"
                  style={{
                    borderColor: hexA(meta.color, 0.55),
                    background: `radial-gradient(ellipse 90% 120% at 0% 0%, ${hexA(meta.color, 0.1)}, transparent 60%), #12100d`,
                    boxShadow: `0 0 0 1px ${hexA(meta.color, 0.55)} inset, 0 14px 30px -20px ${hexA(meta.color, 0.5)}`,
                    transform: swap?.transform,
                    opacity: swap?.opacity,
                    transition: noTrans ? "none" : "opacity 240ms ease, transform 240ms ease",
                  }}
                >
                  <div
                    className="w-[52px] h-[52px] rounded-[13px] inline-flex items-center justify-center shrink-0 border"
                    style={{ background: hexA(meta.color, 0.1), borderColor: hexA(meta.color, 0.55) }}
                  >
                    {meta.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[21px] font-bold tracking-[-0.4px] leading-none truncate">{meta.name}</div>
                  </div>
                </div>
                <button
                  onClick={() => changeGame(1)}
                  aria-label="Next game"
                  className="w-10 shrink-0 inline-flex items-center justify-center text-[#8a7f72] hover:text-[#f5efe6] active:scale-90 transition-[color,transform] duration-150"
                >
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className="flex justify-center gap-1.5 mt-[14px] mb-6">{dots}</div>

              {/* Stake */}
              <div className="flex items-baseline justify-between text-[11.5px] font-semibold uppercase tracking-[1.6px] text-[#5a5247] mb-[11px]">
                <span>Stake ({SOLANA_NETWORK ? cur : "USDC"})</span>
                <span>Per player</span>
              </div>
              <div className="grid grid-cols-4 gap-2.5 mb-3">
                {TIERS[cur].map((v) => {
                  const sel = Math.abs(entered - v) < 1e-9;
                  return (
                    <button
                      key={v}
                      onClick={() => selectChip(v)}
                      className={`cursor-pointer rounded-xl py-3.5 text-center font-mono text-[15px] font-semibold tracking-[-0.2px] border transition-all duration-150 ${
                        sel
                          ? "border-[#ff5722] text-[#ff5722] bg-[rgba(255,87,34,0.08)] shadow-[0_0_0_3px_rgba(255,87,34,0.08)]"
                          : "border-[#322a22] bg-[#0c0a08] text-[#8a7f72] hover:border-[#5a5247] hover:text-[#f5efe6]"
                      }`}
                    >
                      {cur === "USD" ? "$" + v : v}
                    </button>
                  );
                })}
              </div>
              <div className="w-full flex items-center gap-3 bg-[#0c0a08] border border-[#241e18] rounded-[14px] pl-[18px] pr-[15px] py-[15px] mb-[18px] transition-[border-color,box-shadow] duration-150 focus-within:border-[#ff5722] focus-within:shadow-[0_0_0_3px_rgba(255,87,34,0.12)]">
                <span className="text-[22px] font-bold text-[#8a7f72] inline-flex items-center">
                  {cur === "USD" ? "$" : <SolMark />}
                </span>
                <input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[26px] font-bold tracking-[-0.5px] tabular-nums text-[#f5efe6] placeholder-[#5a5247] placeholder:font-normal"
                />
                {SOLANA_NETWORK && (
                  <button
                    onClick={toggleCurrency}
                    disabled={!canToggle}
                    aria-label="Switch currency"
                    className="group/cur shrink-0 inline-flex items-center gap-[5px] rounded-full border border-[#322a22] bg-white/5 py-2 pl-[13px] pr-[10px] text-[13px] font-bold tracking-[0.4px] text-[#f5efe6] transition-[border-color,background-color] duration-150 enabled:hover:border-[#ff5722] enabled:hover:bg-[rgba(255,87,34,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {cur}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-[#8a7f72] transition-colors duration-150 group-hover/cur:text-[#ff5722]">
                      <path d="M8 9l4-4 4 4M8 15l4 4 4-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>

              {!valid && entered > 0 && (
                <div className="-mt-2 mb-[18px] text-[12px] text-amber-400/90">
                  Enter a stake worth ${MIN_STAKE}–${MAX_STAKE.toLocaleString()}.
                </div>
              )}
              {valid && insufficient && (
                <div className="-mt-2 mb-[18px] text-[12px] text-amber-400/90 flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" />
                  Your balance ({fmt(balanceUsd, "USD")}) is below this stake — top up before the match starts.
                </div>
              )}

              {/* Winner takes */}
              <div className="flex items-center justify-between py-4 border-t border-[#241e18]">
                <span className="text-[14px] text-[#8a7f72]">
                  Winner takes <span className="text-[#5a5247]">(after 10% fee)</span>
                </span>
                <span className="font-mono text-[22px] font-bold text-[#5fcf80] tracking-[-0.4px] tabular-nums">
                  {winnerText}
                </span>
              </div>

              {/* Confirm / Add funds */}
              {valid && insufficient ? (
                <button
                  onClick={openWallet}
                  className="mt-1 w-full h-[58px] rounded-[15px] inline-flex items-center justify-center gap-[10px] border border-[#322a22] bg-white/[0.05] hover:bg-white/[0.09] text-[#f5efe6] text-[16.5px] font-bold tracking-[-0.2px] transition-colors duration-150"
                >
                  <Wallet className="w-[18px] h-[18px]" /> Add funds
                </button>
              ) : (
                <button
                  onClick={send}
                  disabled={!canSend}
                  className="group relative overflow-hidden mt-1 w-full h-[58px] rounded-[15px] inline-flex items-center justify-center gap-[11px] bg-[linear-gradient(180deg,#ff8a4a_0%,#ff5722_55%,#ea4a15_100%)] text-[#1a0d05] text-[16.5px] font-bold tracking-[-0.2px] shadow-[0_16px_40px_-14px_rgba(255,87,34,0.65),inset_0_1px_0_rgba(255,255,255,0.28)] transition-[transform,box-shadow] duration-150 enabled:hover:-translate-y-px enabled:hover:shadow-[0_22px_52px_-16px_rgba(255,87,34,0.8),inset_0_1px_0_rgba(255,255,255,0.32)] enabled:active:translate-y-0 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <Loader2 className="w-[18px] h-[18px] animate-spin" />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a0d05" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
                      <line x1="13" y1="19" x2="19" y2="13" />
                      <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
                      <line x1="5" y1="14" x2="9" y2="18" />
                    </svg>
                  )}
                  {sending ? "Sending…" : "Send challenge"}
                  {canSend && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 -translate-x-[110%] bg-[linear-gradient(110deg,transparent_35%,rgba(255,255,255,0.32)_50%,transparent_65%)] transition-transform duration-700 ease-out group-hover:translate-x-[110%]"
                    />
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
